/**
 * useTerrain3D — toggle MapLibre's hillshade/DEM terrain on the map.
 *
 * When `enabled` flips true (desktop only), we wait for the
 * "terrain-dem" raster source to load, then setTerrain + ease the
 * camera to a 60° pitch / -20° bearing for a 3D view. Disabling
 * resets pitch/bearing to 0 and clears the terrain.
 *
 * Mobile/tablet are excluded because the pitch + tile-fetch cost makes
 * pan/zoom janky on mid-range phones — the toggle simply no-ops.
 *
 * Consumers: App.tsx (terrain toggle button).
 */
import { useEffect } from "react";
import type { MapRef } from "react-map-gl/maplibre";
import type { MutableRefObject } from "react";

export function useTerrain3D(
  mapRef: MutableRefObject<MapRef | null>,
  enabled: boolean,
  isMobileOrTablet: boolean,
): void {
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    if (enabled && !isMobileOrTablet) {
      const applyTerrain = () => {
        if (map.getSource("terrain-dem")) {
          map.setTerrain({ source: "terrain-dem", exaggeration: 1.5 });
          map.easeTo({ pitch: 60, bearing: -20, duration: 1000 });
          return true;
        }
        return false;
      };
      if (!applyTerrain()) {
        const onSourceData = (e: { sourceId?: string; isSourceLoaded?: boolean }) => {
          if (e.sourceId === "terrain-dem" && e.isSourceLoaded) {
            applyTerrain();
            map.off("sourcedata", onSourceData);
          }
        };
        map.on("sourcedata", onSourceData);
        return () => { map.off("sourcedata", onSourceData); };
      }
    } else {
      if (map.getTerrain()) map.setTerrain(null);
      map.easeTo({ pitch: 0, bearing: 0, duration: 1000 });
    }
  }, [mapRef, enabled, isMobileOrTablet]);
}
